import BaseEntity from './BaseEntity';

import { EEntityType } from '../types';
import AreaTransformHelper from './2d/transformHelper/areaTransformHelper';
import { Point2dUtils } from '../math/Point2dUtils';
import { EAreaDimensionType, IDimensionPoint } from '../data/dimension/dimension';
import { globalStores } from '../stores';
import { IPoint2d } from '../math/IPoint2d';
import { Curve2dUtils } from '../math/Curve2dUtils';
import { normalizeAngle } from '../math/utils';
import { thresholdValue } from '../math/const';

export default class AreaDimensionTransformEntity extends BaseEntity<EEntityType.AreaTransform> {
  protected override _model2d: AreaTransformHelper;
  private _polygon: IDimensionPoint[] = [];
  private _dimensionType: EAreaDimensionType = EAreaDimensionType.RECT;
  private _controlPointsMap: Map<string, number> = new Map();

  constructor() {
    super({ type: EEntityType.AreaTransform });
    this._model2d = new AreaTransformHelper();
  }

  get model2d() {
    return this._model2d;
  }

  getData() {
    return {
      controlPointMap: this._controlPointsMap,
      dimensionType: this._dimensionType
    };
  }

  private _updateData(polygon: IDimensionPoint[]) {
    const areaDimension = globalStores.dimensionStore.getAreaDim(globalStores.editorStateStore.selected.entityId);
    if (areaDimension) {
      this._dimensionType = areaDimension.type;
      const isRect = this._dimensionType === EAreaDimensionType.RECT;
      this._polygon = [...polygon];
      const handles = isRect
        ? computeRectBoundingBoxOffsets(this._polygon)
        : computePolygonHandleOffsets(this._polygon);
      this._controlPointsMap = new Map(Object.values(handles).map((value) => [value.id, value.offset]));
    }
  }

  override update(polygon: IDimensionPoint[]) {
    polygon = uniquePoints(polygon);
    if (polygon.length >= 3) {
      this._updateData(polygon);
      this._model2d.update(this._polygon);
    }
  }

  clear() {
    this._model2d.clear();
  }
}

function uniquePoints(points: IDimensionPoint[]): IDimensionPoint[] {
  const result: IDimensionPoint[] = [];

  outer: for (const point of points) {
    for (const saved of result) {
      if (Point2dUtils.equals(saved, point)) {
        continue outer;
      }
    }
    result.push(point);
  }

  return result;
}

/**
 * 由矩形四个顶点计算旋转角、宽高以及 8 个控制点的 offset。
 */
function computeRectBoundingBoxOffsets(points: IDimensionPoint[]) {
  if (points.length !== 4) {
    throw new Error('矩形由4个顶点组成');
  }

  const center = Curve2dUtils.calculatePolygonCentroid(points);

  const edge01 = Point2dUtils.subtracted(points[1], points[0]);
  const edge12 = Point2dUtils.subtracted(points[2], points[1]);

  const width = Math.hypot(edge01.x, edge01.y);
  const height = Math.hypot(edge12.x, edge12.y);
  if (width === 0 || height === 0) {
    throw new Error('顶点退化，无法组成矩形');
  }

  const rotation = 0; // 目前矩形面积标注没有旋转
  const ex = normalize(edge01); // 局部 +X
  const ey = normalize(edge12); // 局部 +Y
  const diagonalOffset = Math.atan2(height, width);

  const controlPoints: Array<{ id: string; position: IPoint2d; offset: number }> = [];

  // 四个角点
  for (const point of points) {
    const local = worldToLocal(point, center, ex, ey);
    if (Math.abs(local.x) < thresholdValue || Math.abs(local.y) < thresholdValue) {
      throw new Error('输入顶点不构成矩形角点');
    }

    // 当在1、3象限时为正对角线角，否则为负
    const handleOffset = local.x * local.y >= 0 ? diagonalOffset : -diagonalOffset;

    controlPoints.push({
      id: point.id,
      position: { x: point.x, y: point.y },
      offset: -(rotation + normalizeAngle(handleOffset))
    });
  }

  // 四条边中点
  for (let i = 0; i < 4; i++) {
    const curr = points[i];
    const next = points[(i + 1) % 4];
    const midPoint = {
      x: (curr.x + next.x) / 2,
      y: (curr.y + next.y) / 2
    };
    let offset = 0;
    const local = worldToLocal(midPoint, center, ex, ey);
    if (local.x <= thresholdValue && Math.abs(local.y) > thresholdValue) {
      offset = Math.PI / 2;
    }

    controlPoints.push({
      id: `${curr.id}-${next.id}`,
      position: midPoint,
      offset: -(rotation + normalizeAngle(offset))
    });
  }

  return controlPoints;
}

/**
 * 计算多边形的所有端点和所有边的中点的控制柄的offset
 */
function computePolygonHandleOffsets(points: IDimensionPoint[]) {
  const n = points.length;
  const handles: Array<{ id: string; position: IPoint2d; offset: number }> = [];

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n];
    const curr = points[i];
    const next = points[(i + 1) % n];

    const incoming = { x: curr.x - prev.x, y: curr.y - prev.y }; // prev -> curr
    const outgoing = { x: next.x - curr.x, y: next.y - curr.y }; // curr -> next

    const dirToPrev = normalize({ x: prev.x - curr.x, y: prev.y - curr.y });
    const dirToNext = normalize({ x: next.x - curr.x, y: next.y - curr.y });

    const sum = { x: dirToPrev.x + dirToNext.x, y: dirToPrev.y + dirToNext.y };
    const sumLen = Math.hypot(sum.x, sum.y);

    let bisector: IPoint2d;

    if (sumLen > thresholdValue) {
      bisector = { x: sum.x / sumLen, y: sum.y / sumLen };

      const turn = incoming.x * outgoing.y - incoming.y * outgoing.x;
      if (turn < 0) {
        // 凹顶点 -> 把向量翻转才能指向内部
        bisector = { x: -bisector.x, y: -bisector.y };
      }
    } else {
      // 共线，使用内法线
      const edgeDir = normalize(outgoing); // 和 incoming 共线
      bisector = { x: -edgeDir.y, y: edgeDir.x }; // 逆时针 -> 左法线 = 内侧
    }

    const offset = -normalizeAngle(Math.atan2(bisector.y, bisector.x));
    handles.push({
      id: curr.id,
      position: { x: curr.x, y: curr.y },
      offset
    });
  }

  for (let i = 0; i < n; i++) {
    const curr = points[i];
    const next = points[(i + 1) % n];

    const midpoint = {
      x: (curr.x + next.x) / 2,
      y: (curr.y + next.y) / 2
    };

    const edgeVec = { x: next.x - curr.x, y: next.y - curr.y };
    const edgeDir = normalize(edgeVec);

    // 逆时针多边形的外法线 = 顺时针旋转 90°
    const outwardNormal = { x: edgeDir.y, y: -edgeDir.x };
    const offset = -normalizeAngle(Math.atan2(outwardNormal.y, outwardNormal.x));

    handles.push({
      id: `${curr.id}-${next.id}`,
      position: midpoint,
      offset
    });
  }

  return handles;
}

function normalize(v: IPoint2d): IPoint2d {
  const len = Math.hypot(v.x, v.y);
  if (len === 0) {
    throw new Error('无法对零向量归一化');
  }
  return { x: v.x / len, y: v.y / len };
}

/**
 * 将世界坐标点转换到以中心为原点、(ex, ey) 为基向量的局部坐标。
 */
function worldToLocal(point: IPoint2d, center: IPoint2d, ex: IPoint2d, ey: IPoint2d): IPoint2d {
  const v = Point2dUtils.subtracted(point, center);
  return {
    x: v.x * ex.x + v.y * ex.y,
    y: v.x * ey.x + v.y * ey.y
  };
}
